import React, { useCallback } from 'react';

import { useChecklistContext } from '@features/Checklist/model/hooks/useChecklistContext';
import { IChecklistTask } from '@features/Checklist/model/types';

interface IUseChecklistItemHandlers {
    itemData: IChecklistTask;
    clearErrorCb: () => void;
    addErrorCb: () => void;
    modalTitleKeydownCb: () => void;
    isError: boolean;
}

export const useChecklistItemHandlers = ({
    itemData,
    clearErrorCb,
    addErrorCb,
    modalTitleKeydownCb,
    isError,
}: IUseChecklistItemHandlers) => {
    const {
        onAddChecklistItem,
        onDeleteChecklistItem,
        onChangeChecklistItemText,
        onToggleChecklistItem,
    } = useChecklistContext();
    const onAddCheckListItemHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onAddChecklistItem(itemData.clientId);
    };
    const onDeleteCheckListItemHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onDeleteChecklistItem(itemData.clientId);
    };

    const onChangeChecklistItemHandler = useCallback(
        (value: string) => {
            if (itemData.isChecked) return;
            if (isError) {
                clearErrorCb();
            }

            onChangeChecklistItemText(itemData.clientId, value);
        },
        [clearErrorCb, isError, itemData.clientId, itemData.isChecked, onChangeChecklistItemText]
    );

    const onModalTitleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key !== 'Enter') return;

            e.preventDefault();
            e.stopPropagation();

            const trimmedValue = itemData.text?.trim();

            if (!trimmedValue) {
                addErrorCb();
                return;
            }
            modalTitleKeydownCb();
        },
        [modalTitleKeydownCb, addErrorCb, itemData.text]
    );
    const onCheckedItem = () => {
        if (itemData.text.trim().length === 0) {
            addErrorCb();
            return;
        }
        onToggleChecklistItem(itemData.clientId);
    };

    return {
        onAddCheckListItemHandler,
        onChangeChecklistItemHandler,
        onDeleteCheckListItemHandler,
        onModalTitleKeyDown,
        onCheckedItem,
    };
};
